import {
	Box,
	Button,
	Card,
	CardContent,
	Grid2,
	TextField,
	Typography
} from "@mui/material";
import { useFormik } from "formik";
import { PhoneNumberUtil } from "google-libphonenumber";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import { useFreelancerContext } from "../../../contexts/useFreelancerContext";
import { useNavigationInterceptorContext } from "../../../contexts/useNavigationInterceptorContext";
import {
	usePatchMyPersonalInformationFreelancerOnboardingStep,
	useSaveMyPersonalInformationFreelancerOnboardingProgress
} from "../../../hooks/useFreelancerProfile";
import { FreelancerOnboardingFormStep } from "../../../models/enums/FreelancerOnboardingFormStep";
import { zoullooColors } from "../../../theme";
import {
	AddressViewModel,
	FreelancerOnboardingMyPersonalInformationStepViewModel,
	FreelancerPhotoViewModel as FreelancerPictureViewModel
} from "../../../ViewModels/FreelancerOnboardingMyPersonalInformationStepViewModel";
import PhoneInput from "../../UI/PhoneInput";
import PlacesAutocomplete from "../../UI/PlacesAutocomplete";
import ProfilePicture, {
	ProfilePictureValidationState,
	ProfilePictureValue
} from "../../UI/ProfilePicture";
import i18n from "../../../i18n/i18n";
import { useNavigate } from "react-router-dom";
import { FreelancerOnboardingStatus } from "../../../models/enums/FreelancerOnboardingStatus";

const FreelancerOnboardingMyPersonalInformation: React.FC = () => {
	const [profilePictureValidationState, setProfilePictureValidationState] =
		useState<ProfilePictureValidationState | null>(null);
	const {
		setCurrentOnboardingFormStep,
		setNextOnboardingFormStep: setNextOnboardingStep,
		setFreelancerOnboardingMyPersonalInformationStepViewModel,
		freelancerQueryResult
	} = useFreelancerContext();
	const { data: freelancer } = freelancerQueryResult;
	const patchMyPersonalInformationFreelancerOnboardingStep =
		usePatchMyPersonalInformationFreelancerOnboardingStep();
	const saveMyPersonalInformationFreelancerOnboardingProgress =
		useSaveMyPersonalInformationFreelancerOnboardingProgress({ hideAllSnackbars: true });
	const { t } = useTranslation();
	const { interceptNavigation } = useNavigationInterceptorContext();
	const [
		isAddressValueChangedOnLoadBecauseOfBeingTranslated,
		setIsAddressValueChangedOnLoadBecauseOfBeingTranslated
	] = useState(true);
	const navigate = useNavigate();

	// Phone validation function using google-libphonenumber
	const phoneUtil = PhoneNumberUtil.getInstance();
	const isPhoneValid = (phone: string) => {
		// Handle empty or null phone numbers
		if (!phone || phone.trim() === "" || (phone.startsWith("+") && phone.length <= 4)) {
			return false; // Phone is required
		}

		try {
			return phoneUtil.isValidNumber(phoneUtil.parseAndKeepRawInput(phone));
		} catch (error) {
			console.error("Phone validation error:", error);
			return false;
		}
	};

	// Validation schema
	const validationSchema = Yup.object().shape({
		firstName: Yup.string().required(
			t("freelancerOnboardingMyPersonalInformation.validation.firstNameRequired")
		),
		lastName: Yup.string().required(
			t("freelancerOnboardingMyPersonalInformation.validation.lastNameRequired")
		),
		email: Yup.string().test(
			"not-modified",
			t("freelancerOnboardingMyPersonalInformation.validation.emailIsReadOnly"),
			function (value) {
				return value === freelancer?.user.email;
			}
		),
		phoneNumber: Yup.string()
			.required(t("freelancerOnboardingMyPersonalInformation.validation.phoneRequired"))
			.test(
				"phone-valid",
				t("freelancerOnboardingMyPersonalInformation.validation.phoneInvalid"),
				value => (value ? isPhoneValid(value) : false)
			),
		selectedAddress: Yup.object().required(
			t("freelancerOnboardingMyPersonalInformation.validation.addressRequired")
		),
		profilePhoto: Yup.object()
			.nullable()
			.test("profile-photo-valid", "", function (value) {
				const photo = value as ProfilePictureValue | null | undefined;

				if (!photo?.urlPath) {
					setProfilePictureValidationState(null);
					return true;
				}

				const validationState: ProfilePictureValidationState = {
					format: true,
					dimensions: true,
					size: true
				};
				const allowedExtensions = [".jpg", ".jpeg", ".jfif", ".png", ".webp"];
				const fileName = photo?.name?.toLowerCase() || "";
				const hasValidExtension = allowedExtensions.some(ext => fileName.endsWith(ext));

				if (fileName !== "") {
					validationState.format = hasValidExtension;
				}

				// Validate dimensions (minimum 400x400) - image is already loaded in DOM
				if (photo && !photo.urlPath?.startsWith("blob:")) {
					const img = document.querySelector(
						`img[src$="${photo.urlPath}"]`
					) as HTMLImageElement;

					if (img && img.naturalWidth !== 0 && img.naturalHeight !== 0) {
						validationState.dimensions =
							img.naturalWidth >= 400 && img.naturalHeight >= 400;
					}
				}

				setProfilePictureValidationState(validationState);

				return (
					validationState.format && validationState.size && validationState.dimensions
				);
			})
	});

	const formik = useFormik<FreelancerOnboardingMyPersonalInformationStepViewModel>({
		initialValues: {
			firstName: freelancer?.user?.firstName || "",
			lastName: freelancer?.user?.lastName || "",
			email: freelancer?.user?.email || "",
			phoneNumber: freelancer?.phoneNumber || "",
			selectedAddress: freelancer?.address
				? {
						id: freelancer.address.id || 0,
						fullAddress: freelancer.address.fullAddress || "",
						street: freelancer.address.street || "",
						streetNumber: freelancer.address.streetNumber || "",
						postalCode: freelancer.address.postalCode || "",
						city: freelancer.address.city || "",
						region: freelancer.address.region || "",
						country: freelancer.address.country || "",
						countryCode: freelancer.address.countryCode || "",
						latitude: freelancer.address.latitude || 0,
						longitude: freelancer.address.longitude || 0,
						placeId: freelancer.address.placeId || "",
						formattedAddress: freelancer.address.formattedAddress || ""
				  }
				: null,
			profilePhoto: freelancer?.picture
				? {
						id: freelancer.picture.id || 0,
						name: freelancer.picture.name || "",
						urlPath: freelancer.picture.urlPath || "",
						content: "",
						freelancerId: freelancer.picture.freelancerId || 0
				  }
				: null,
			form: { isValid: false, isSubmitting: false, isDirty: false }
		},
		validationSchema,
		enableReinitialize: true,
		onSubmit: async values => {
			try {
				await patchMyPersonalInformationFreelancerOnboardingStep.mutateAsync({
					freelancer: freelancer!,
					freelancerOnboardingMyPersonalInformationStepViewModel: {
						...values,
						form: { isValid: formik.isValid, isSubmitting: true, isDirty: formik.dirty }
					},
					newStep: FreelancerOnboardingFormStep.MyPersonalInformation
				});

				if (
					freelancer?.profile.onboarding.currentStatus !==
					FreelancerOnboardingStatus.Accepted
				) {
					navigate("/my-application", { state: { redirectToMyApplication: true } });
				}
			} catch (error) {
				console.error("Error submitting form:", error);
				formik.setFieldValue("form.isSubmitting", false);
			}
		}
	});

	// Convert Google Places result to AddressViewModel
	const convertPlaceResultToAddressViewModel = useCallback(
		(placeResult: google.maps.places.PlaceResult): AddressViewModel => {
			const addressComponents = placeResult.address_components || [];
			const getComponent = (type: string) =>
				addressComponents.find(component => component.types.includes(type))?.long_name ||
				"";

			return {
				id: 0,
				fullAddress: placeResult.formatted_address || "",
				street: getComponent("route"),
				streetNumber: getComponent("street_number"),
				postalCode: getComponent("postal_code"),
				city: getComponent("locality") || getComponent("administrative_area_level_2"),
				region: getComponent("administrative_area_level_1"),
				country: getComponent("country"),
				countryCode: addressComponents.find(component =>
					component.types.includes("country")
				)?.short_name,
				latitude: placeResult.geometry?.location?.lat(),
				longitude: placeResult.geometry?.location?.lng(),
				placeId: placeResult.place_id,
				formattedAddress: placeResult.formatted_address
			};
		},
		[]
	);

	// Update context with form values and validity state
	useEffect(() => {
		setFreelancerOnboardingMyPersonalInformationStepViewModel({
			...formik.values,
			form: {
				isValid: validationSchema.isValidSync(formik.values),
				isSubmitting: formik.isSubmitting,
				isDirty: formik.dirty
			}
		});

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [formik.values]);

	useEffect(() => {
		setTimeout(() => {
			setFreelancerOnboardingMyPersonalInformationStepViewModel({
				...formik.values,
				form: {
					isValid: validationSchema.isValidSync(formik.values),
					isSubmitting: formik.isSubmitting,
					isDirty: !isAddressValueChangedOnLoadBecauseOfBeingTranslated
				}
			});
		}, 400);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isAddressValueChangedOnLoadBecauseOfBeingTranslated, i18n.language]);

	return (
		<Card
			sx={{
				marginX: "32px",
				marginY: "48px"
			}}
		>
			<CardContent sx={{ userSelect: "text" }}>
				<Typography variant="h2" color="textPrimary" gutterBottom sx={{ mb: 3 }}>
					{t("freelancerOnboardingMyPersonalInformation.title")}
				</Typography>

				<Typography variant="body1" sx={{ mb: 4 }}>
					{t("freelancerOnboardingMyPersonalInformation.description")}
				</Typography>

				<form onSubmit={formik.handleSubmit}>
					{/* Contact Information Section */}
					<Typography variant="h4" sx={{ mb: 3 }}>
						{t("freelancerOnboardingMyPersonalInformation.contactInfo")}
					</Typography>

					<Grid2 container spacing={3} sx={{ mb: 4 }}>
						<Grid2 size={{ xs: 12, md: 6 }}>
							<TextField
								fullWidth
								name="firstName"
								value={formik.values.firstName}
								onChange={formik.handleChange}
								onBlur={formik.handleBlur}
								label={`${t("freelancerOnboardingMyPersonalInformation.firstName")} *`}
								variant="outlined"
								error={formik.touched.firstName && Boolean(formik.errors.firstName)}
								helperText={formik.touched.firstName && formik.errors.firstName}
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, md: 6 }}>
							<TextField
								fullWidth
								name="lastName"
								value={formik.values.lastName}
								onChange={formik.handleChange}
								onBlur={formik.handleBlur}
								label={`${t("freelancerOnboardingMyPersonalInformation.lastName")} *`}
								variant="outlined"
								error={formik.touched.lastName && Boolean(formik.errors.lastName)}
								helperText={formik.touched.lastName && formik.errors.lastName}
							/>
						</Grid2>
					</Grid2>

					<Grid2 container spacing={3} sx={{ mb: 4 }}>
						<Grid2 size={{ xs: 12, md: 6 }}>
							<TextField
								fullWidth
								name="email"
								type="email"
								value={formik.values.email}
								onChange={formik.handleChange}
								onBlur={formik.handleBlur}
								label={t("freelancerOnboardingMyPersonalInformation.email")}
								variant="outlined"
								error={formik.touched.email && Boolean(formik.errors.email)}
								helperText={formik.touched.email && formik.errors.email}
								slotProps={{
									input: {
										readOnly: true
									}
								}}
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, md: 6 }}>
							<PhoneInput
								value={formik.values.phoneNumber || ""}
								onChange={(phone: string) => {
									formik.setFieldValue(
										"phoneNumber",
										!formik.touched.phoneNumber &&
											phone.startsWith("+") &&
											phone.length <= 4
											? ""
											: phone
									);
								}}
								onBlur={formik.handleBlur}
								name="phoneNumber"
								required={true}
								error={formik.touched.phoneNumber && Boolean(formik.errors.phoneNumber)}
								helperText={formik.touched.phoneNumber && formik.errors.phoneNumber}
							/>
						</Grid2>
					</Grid2>

					<Grid2 container spacing={3} sx={{ mb: 4 }}>
						<Grid2 size={{ xs: 12 }}>
							<PlacesAutocomplete
								name="selectedAddress"
								value={
									formik.values.selectedAddress
										? ({
												place_id: formik.values.selectedAddress.placeId,
												formatted_address: formik.values.selectedAddress.fullAddress
										  } as google.maps.places.PlaceResult)
										: null
								}
								onChange={place => {
									setTimeout(() => {
										setIsAddressValueChangedOnLoadBecauseOfBeingTranslated(
											place?.place_id === formik.values.selectedAddress?.placeId
										);
									}, 200);

									if (!place) {
										formik.setFieldValue("selectedAddress", null);
										return;
									}

									const addressViewModel = convertPlaceResultToAddressViewModel(place);

									formik.setFieldValue("selectedAddress", addressViewModel);
								}}
								onBlur={() => {
									formik.setFieldTouched("selectedAddress", true);
								}}
								types={["address"]}
								componentRestrictions={{ country: [] }}
								fields={[
									"address_components",
									"formatted_address",
									"geometry",
									"place_id"
								]}
								minInputLength={3}
								label={`${t("freelancerOnboardingMyPersonalInformation.address")} *`}
								placeholder={t("freelancerOnboardingMyPersonalInformation.selectAddress")}
								variant="outlined"
								fullWidth
								error={
									formik.touched.selectedAddress && Boolean(formik.errors.selectedAddress)
								}
								helperText={
									formik.touched.selectedAddress && formik.errors.selectedAddress
								}
							/>
						</Grid2>
					</Grid2>

					{/* Profile Photo Section */}
					<ProfilePicture
						value={formik.values.profilePhoto}
						onChange={(photo: ProfilePictureValue | null) => {
							// Convert ProfilePictureValue to FreelancerPictureViewModel if needed
							if (photo) {
								const freelancerPhoto: FreelancerPictureViewModel = {
									id: photo.id,
									name: photo.name || "",
									urlPath: photo.urlPath || "",
									content: photo.content,
									freelancerId: freelancer?.id
								};
								formik.setFieldValue("profilePhoto", freelancerPhoto);
							} else {
								formik.setFieldValue("profilePhoto", null);
							}
						}}
						onBlur={() => formik.setFieldTouched("profilePhoto", true)}
						name="profilePhoto"
						size={250}
						showValidation={true}
						showHelp={true}
						showExamples={false}
						getPhotoUrl={(photo: ProfilePictureValue) => {
							if (photo.content) {
								return photo.urlPath || "";
							}

							const baseUrl = process.env.REACT_APP_ZOULLOO_FILE_STORAGE_BASE_URL;
							const containerName =
								process.env.REACT_APP_FREELANCER_PICTURES_CONTAINER_NAME;

							return `${baseUrl}/${containerName}/${photo.urlPath}`;
						}}
						setProfilePictureValidationState={setProfilePictureValidationState}
						profilePictureValidationState={profilePictureValidationState}
					/>

					{/* Action Buttons */}
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							justifyContent: "center",
							gap: 3,
							mt: 15
						}}
					>
						<Button
							type="submit"
							variant="contained"
							size="large"
							color="secondary"
							disabled={formik.isSubmitting}
							sx={{
								backgroundColor: zoullooColors.secondary.main,
								width: "45%",
								color: zoullooColors.secondary.contrastText,
								py: 1.5
							}}
						>
							{freelancer?.profile.onboarding.currentStatus !==
							FreelancerOnboardingStatus.Accepted
								? t("freelancerOnboardingMyPersonalInformation.buttons.submit")
								: t("common.save")}
						</Button>
						{freelancer?.profile.onboarding.currentStatus !==
							FreelancerOnboardingStatus.Accepted && (
							<Button
								variant="outlined"
								size="large"
								color="secondary"
								onClick={async () => {
									interceptNavigation({
										onValidNavigation: async () => {
											try {
												await saveMyPersonalInformationFreelancerOnboardingProgress.mutateAsync(
													{
														freelancerOnboardingMyPersonalInformationStepViewModel:
															formik.values,
														freelancer: freelancer!,
														newStep:
															FreelancerOnboardingFormStep.MyProfessionalInformation
													}
												);

												setCurrentOnboardingFormStep(
													FreelancerOnboardingFormStep.MyProfessionalInformation
												);
											} catch (error) {
												console.error(
													"Error returning back to the previous step:",
													error
												);
											}
										},
										onBlockedNavigation: () => {
											setNextOnboardingStep(
												FreelancerOnboardingFormStep.MyProfessionalInformation
											);
										}
									});
								}}
								sx={{ width: "45%" }}
							>
								{t("freelancerOnboardingMyPersonalInformation.buttons.back")}
							</Button>
						)}
					</Box>
				</form>
			</CardContent>
		</Card>
	);
};

export default FreelancerOnboardingMyPersonalInformation;
